// Case Buddy domain module generated from CaseBuddyCore.js.
// Spreadsheet-shaped rows remain the source of truth.
import { getCaseBuddyHistoricalPriceReferenceMetadata } from '../CaseBuddyHistoricalUsdReference.js';
import { caseBuddyHashString, firstNonEmptyEntryValue, normalizeCell, parseCaseUtcDate } from './CaseBuddyShared.js';
import { buildTopLevelMainEntryDescriptors, parseCaseEntries } from './CaseBuddySheetData.js';
import {
  CASE_TRACE_DIRECTION_FORWARD,
  CASE_TRACE_DIRECTION_REVERSE,
  buildCaseTraceDirectionContextByRowIndex,
  getCaseTraceDirectionForEntry,
  getCaseTraceDirectionRelevantAttribution,
  normalizeCaseTraceDirection
} from './CaseBuddyTraceDirection.js';
import { CASE_COLUMN_HEADERS, normalizeCaseMatrix } from './CaseBuddyWorkbookState.js';

export const CASE_WHAT_WENT_WHERE_COLUMN_COUNT = 17;

export const CASE_WHAT_WENT_WHERE_HEADERS = Object.freeze(CASE_COLUMN_HEADERS.slice(0, CASE_WHAT_WENT_WHERE_COLUMN_COUNT));

export const CASE_WHAT_WENT_WHERE_CATEGORY_ORDER = Object.freeze(['Dormant Wallet', 'Exchange', 'Non-Exchange', 'Unknown', 'Lost/Dismissed', 'Other']);

export const CASE_WHAT_WENT_WHERE_STABLECOINS = Object.freeze(new Set(['USDT', 'USDC', 'USDD', 'DAI']));

export const CASE_WHAT_WENT_WHERE_MAX_TRACE_DEPTH = 30;

export const CASE_WHAT_WENT_WHERE_SYNTHETIC_DORMANT_NOTE = 'Path ends. Partial dormant funds remain at the receiving address while the remainder continues.';

export const CASE_WHAT_WENT_WHERE_DORMANT_NOTE_REGEX = /\b(?:A portion of\s+(?:the\s+victim's\s+)?(?:the\s+)?funds remains at\s+(?:the\s+receiving|this)\s+address|(?:The victim's funds|The funds)\s+remain at\s+(?:the\s+receiving|this)\s+address)\b/i;

export const CASE_WHAT_WENT_WHERE_DORMANT_AMOUNT_REGEX = /\b(?:A portion of\s+(?:the\s+victim's\s+)?(?:the\s+)?funds remains at\s+(?:the\s+receiving|this)\s+address|(?:The victim's funds|The funds)\s+remain at\s+(?:the\s+receiving|this)\s+address)\s*\(([^)]+)\)/i;

export function normalizeCaseWwwHash(value = '') {
  const text = normalizeCell(value);
  if (!text) return '';
  return /^0x/i.test(text) ? text.toLowerCase() : text;
}

export function normalizeCaseWwwToken(value = '') {
  return normalizeCell(value).toUpperCase();
}

export function caseWwwTokenMatches(left = '', right = '') {
  const a = normalizeCaseWwwToken(left);
  const b = normalizeCaseWwwToken(right);
  return !!a && a === b;
}

export function caseWwwIsStablecoin(token = '') {
  return CASE_WHAT_WENT_WHERE_STABLECOINS.has(normalizeCaseWwwToken(token));
}

export function parseCaseWwwNumeric(value = '') {
  const text = normalizeCell(value).replace(/[$,]/g, '').trim();
  if (!text) return null;
  const match = text.match(/-?\d*\.?\d+(?:e[+-]?\d+)?/i);
  if (!match) return null;
  const parsed = Number(match[0]);
  return Number.isFinite(parsed) ? parsed : null;
}

export function parseCaseWwwAmountTokenFromColumns(amountValue = '', tokenValue = '') {
  const amount = parseCaseWwwNumeric(amountValue);
  const token = normalizeCell(tokenValue);
  if (!Number.isFinite(amount) || !token) return null;
  return { amount, token };
}

export function parseCaseWwwFundsFollowed(value = '') {
  const text = normalizeCell(value).replace(/[$,]/g, '').trim();
  if (!text) return null;
  const match = text.match(/^(-?\d*\.?\d+(?:e[+-]?\d+)?)\s+(.+)$/i);
  if (!match) return null;
  const amount = Number(match[1]);
  const token = normalizeCell(match[2]).replace(/[;,]+$/, '').split(/\s+/)[0];
  if (!Number.isFinite(amount) || !token) return null;
  return { amount, token };
}

export function formatCaseWwwUsd(value = 0) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '';
  const absolute = Math.abs(numeric).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return numeric < 0 ? `-$${absolute}` : `$${absolute}`;
}

export function formatCaseWwwUsdCell(value = 0) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '';
  return numeric.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function formatCaseWwwTokenAmount(value = 0, token = '') {
  const numeric = Number(value);
  const normalizedToken = normalizeCell(token);
  if (!Number.isFinite(numeric) || !normalizedToken) return '';
  const decimals = caseWwwIsStablecoin(normalizedToken) ? 2 : 4;
  return `${numeric.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })} ${normalizedToken}`;
}

export function parseCaseWwwUsdReference(value = '') {
  const text = normalizeCell(value).replace(/\bUSDT\b|\bUSD\b/gi, '').replace(/[$,]/g, '').trim();
  if (!text) return null;
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : null;
}

export function getCaseWwwMainReferenceAmountToken(entryDescriptor = {}) {
  const leadRow = entryDescriptor.entryRows?.[0]?.row || [];
  const fundsFollowed = parseCaseWwwFundsFollowed(leadRow[12]);
  if (fundsFollowed) return { ...fundsFollowed, source: 'Column M Funds Followed' };
  const amountToken = parseCaseWwwAmountTokenFromColumns(leadRow[5], leadRow[6]);
  if (amountToken) return { ...amountToken, source: 'Columns F/G Amount + Token' };
  return null;
}

export function buildCaseWwwReferenceRows(mainData = []) {
  return buildTopLevelMainEntryDescriptors(mainData).map((entry, index) => {
    const lead = entry.entryRows?.[0] || { rowIndex: entry.startRow, row: [] };
    const row = Array.isArray(lead.row) ? lead.row : [];
    const amountToken = getCaseWwwMainReferenceAmountToken(entry);
    const usdValue = parseCaseWwwUsdReference(row[7]);
    const dateInfo = parseCaseUtcDate(firstNonEmptyEntryValue(entry.entryRows, 4) || row[4]);
    const isUsable = !!amountToken && Number.isFinite(usdValue) && amountToken.amount > 0;
    const problemText = isUsable
      ? ''
      : [
        amountToken ? '' : 'Missing usable amount/token from Column M or Columns F/G.',
        Number.isFinite(usdValue) ? '' : 'Missing usable Column H USD value.',
        amountToken && amountToken.amount <= 0 ? 'Reference amount must be greater than zero.' : ''
      ].filter(Boolean).join(' ');
    const key = `www-main-${entry.startRow}-${caseBuddyHashString(`${entry.hash}|${entry.currentLabel}|${index}`)}`;
    return {
      key,
      index,
      rowIndex: lead.rowIndex,
      rowNumber: lead.rowIndex + 1,
      pathLabel: normalizeCell(row[0]) || entry.currentLabel,
      transactionHash: normalizeCell(entry.hash || row[1]),
      utcDate: dateInfo.isValid ? dateInfo.canonical : normalizeCell(row[4]),
      dateIsValid: !!dateInfo.isValid,
      referenceAmount: amountToken?.amount ?? null,
      referenceToken: amountToken?.token || '',
      referenceSource: amountToken?.source || '',
      referenceAmountDisplay: amountToken ? formatCaseWwwTokenAmount(amountToken.amount, amountToken.token) : '',
      currentColumnH: normalizeCell(row[7]),
      currentUsdValue: Number.isFinite(usdValue) ? usdValue : null,
      currentUsdDisplay: Number.isFinite(usdValue) ? formatCaseWwwUsd(usdValue) : '',
      usdtPerUnit: isUsable ? usdValue / amountToken.amount : null,
      isUsable,
      problemText,
      searchText: [lead.rowIndex + 1, row[0], entry.hash, row[4], row[5], row[6], row[7], row[12], problemText]
        .map(value => normalizeCell(value).toLowerCase())
        .join(' ')
    };
  });
}


export function buildCaseWwwUsdReferenceReviewState(mainData = [], workflowState = {}, options = {}) {
  const referenceRows = buildCaseWwwReferenceRows(mainData);
  const usableReferenceCount = referenceRows.filter(row => row.isUsable).length;
  const missingUsdReferenceCount = referenceRows.filter(row => !row.isUsable).length;
  const estimateableMissingCount = referenceRows.filter(row => !row.isUsable && !!row.referenceToken && Number.isFinite(row.referenceAmount) && row.dateIsValid).length;
  const currentBasisHash = `www-usd-references-${caseBuddyHashString(JSON.stringify({
    references: referenceRows.map(row => [
      row.rowIndex,
      row.pathLabel,
      row.transactionHash,
      row.utcDate,
      row.referenceAmount,
      row.referenceToken,
      row.currentColumnH,
      row.isUsable
    ])
  }))}`;
  const review = workflowState?.whatWentWhereUsdReferenceReview || null;
  const revision = Number(options.caseDataRevision) || 0;
  const reviewIsCurrent = !!review?.reviewedAtUtc && review.basisHash === currentBasisHash && Number(review.revision) === revision;
  const canReview = referenceRows.length > 0 && missingUsdReferenceCount === 0;

  return {
    caseId: normalizeCell(options.caseId),
    referenceRows,
    referenceRowCount: referenceRows.length,
    usableReferenceCount,
    missingUsdReferenceCount,
    estimateableMissingCount,
    canReview,
    canBuild: canReview,
    reviewIsCurrent,
    currentBasisHash,
    reviewedAtUtc: review?.reviewedAtUtc || '',
    reviewedBySource: review?.source || '',
    priceReferenceMetadata: getCaseBuddyHistoricalPriceReferenceMetadata(),
    summary: referenceRows.length === 0
      ? 'No top-level main transactions were found for Column H USD reference review.'
      : missingUsdReferenceCount > 0
        ? `${usableReferenceCount}/${referenceRows.length} top-level main transaction${referenceRows.length === 1 ? '' : 's'} have usable Column H USD reference values. ${missingUsdReferenceCount} still need attention before deliverable buildout.`
        : reviewIsCurrent
          ? `All ${referenceRows.length} top-level Column H USD reference value${referenceRows.length === 1 ? '' : 's'} are reviewed for the current case data.`
          : `All ${referenceRows.length} top-level Column H USD reference value${referenceRows.length === 1 ? '' : 's'} are filled. Review and mark this step complete before building deliverables.`
  };
}

export function buildCaseWwwUsdReferenceAuditRows(state = {}, actionLabel = 'Column H USD reference review') {
  const rows = Array.isArray(state.referenceRows) ? state.referenceRows : [];
  return [
    ['Case Buddy Column H USD Reference Audit', '', '', '', '', '', '', '', '', 'Audit only. Main Data Column H stores top-level USD reference values used later by What Went Where.'],
    ['Action', actionLabel, 'Reference Rows', state.referenceRowCount || 0, 'Usable', state.usableReferenceCount || 0, 'Missing', state.missingUsdReferenceCount || 0, 'Basis', state.currentBasisHash || ''],
    ['Status', state.summary || '', 'Reviewed', state.reviewIsCurrent ? 'Yes' : 'No', 'Case', state.caseId || ''],
    [],
    ['Row', 'Path Label', 'Transaction Hash', 'UTC Date', 'Reference Amount', 'Column H USD Value', 'Status', 'Issue'],
    ...rows.map(row => [
      row.rowNumber,
      row.pathLabel || '',
      row.transactionHash || '',
      row.utcDate || '',
      row.referenceAmountDisplay || '',
      row.currentColumnH || '',
      row.isUsable ? 'Usable' : 'Needs value',
      row.problemText || ''
    ])
  ];
}

export function normalizeCaseWwwEntries(entries = []) {
  return (Array.isArray(entries) ? entries : []).map((entry, index) => {
    const first = entry.rows?.[0]?.row || [];
    return {
      ...entry,
      index,
      txnHash: normalizeCell(entry.hash || first[1]),
      pathLabel: normalizeCell(first[0]),
      firstRow: first
    };
  });
}

export function buildCaseWwwEntryLookup(entries = []) {
  const byStartRow = new Map();
  const byHash = new Map();
  entries.forEach((entry, index) => {
    byStartRow.set(entry.startRow, index);
    const hashKey = normalizeCaseWwwHash(entry.txnHash);
    if (!hashKey) return;
    if (!byHash.has(hashKey)) byHash.set(hashKey, []);
    byHash.get(hashKey).push(index);
  });
  return { byStartRow, byHash };
}

export function findCaseWwwEntryIndexForMain(mainTransaction = {}, entries = [], lookup = {}) {
  if (lookup.byStartRow?.has(mainTransaction.rowIndex)) return lookup.byStartRow.get(mainTransaction.rowIndex);
  const indices = lookup.byHash?.get(normalizeCaseWwwHash(mainTransaction.transactionHash)) || [];
  if (indices.length === 0) return undefined;
  if (indices.length === 1) return indices[0];
  const pathLabel = normalizeCell(mainTransaction.pathLabel).toLowerCase();
  const exact = indices.find(index => normalizeCell(entries[index]?.pathLabel).toLowerCase() === pathLabel);
  if (exact != null) return exact;
  return indices[0];
}

export function caseWwwHasDormantFundsLanguage(notes = '') {
  return CASE_WHAT_WENT_WHERE_DORMANT_NOTE_REGEX.test(normalizeCell(notes));
}

export function parseCaseWwwDormantAmountFromNotes(notes = '') {
  const match = normalizeCell(notes).match(CASE_WHAT_WENT_WHERE_DORMANT_AMOUNT_REGEX);
  if (!match) return null;
  const parsed = parseCaseWwwFundsFollowed(match[1]);
  if (!parsed) return null;
  return { ...parsed, rawText: normalizeCell(match[1]) };
}

export function isCaseWwwPathEndingRow(row = []) {
  return /\bPath ends\./i.test(normalizeCell(row?.[16]));
}

export function buildCaseWwwSyntheticDormantRow(sourceRow = [], dormantAmountToken = null) {
  const row = normalizeCaseMatrix([sourceRow], 1, CASE_WHAT_WENT_WHERE_COLUMN_COUNT)[0].slice(0, CASE_WHAT_WENT_WHERE_COLUMN_COUNT);
  if (dormantAmountToken?.amount != null) row[5] = String(dormantAmountToken.amount);
  if (dormantAmountToken?.token) row[6] = dormantAmountToken.token;
  if (dormantAmountToken?.rawText) row[12] = dormantAmountToken.rawText;
  row[16] = CASE_WHAT_WENT_WHERE_SYNTHETIC_DORMANT_NOTE;
  return row;
}

export function getCaseWwwGeneratedPathEndingRowsForEntry(entry = {}) {
  const generated = [];
  (entry.rows || []).forEach((entryRow) => {
    const row = Array.isArray(entryRow.row) ? entryRow.row : [];
    if (isCaseWwwPathEndingRow(row)) {
      generated.push({ row, rowIndex: entryRow.rowIndex, synthetic: false });
      return;
    }
    const noteText = normalizeCell(row[9]);
    if (!noteText || /\bPath ends\./i.test(noteText)) return;
    const dormantAmount = parseCaseWwwDormantAmountFromNotes(noteText);
    if (!dormantAmount) return;
    generated.push({ row: buildCaseWwwSyntheticDormantRow(row, dormantAmount), rowIndex: entryRow.rowIndex, synthetic: true });
  });
  return generated;
}

export function getCaseWwwEntryNotes(entry = {}) {
  return (entry.rows || []).map(item => normalizeCell(item.row?.[9])).filter(Boolean).join(' | ');
}

export function buildCaseWwwEntityKeywords(effectiveCurator = {}, kind = 'exchange') {
  const list = kind === 'nonExchange' ? effectiveCurator.nonExchange : effectiveCurator.exchange;
  const seen = new Set();
  return (Array.isArray(list) ? list : [])
    .map(item => normalizeCell(item?.name || item?.presentedName || item?.canonicalName || item))
    .filter(Boolean)
    .filter((value) => {
      const key = value.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((left, right) => normalizeCell(right).length - normalizeCell(left).length);
}

export function findCaseWwwEntityInText(text = '', keywords = []) {
  const source = normalizeCell(text);
  if (!source) return '';
  for (const keyword of keywords) {
    const escaped = String(keyword).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(^|[^A-Za-z0-9])(${escaped})(?![A-Za-z0-9*])`);
    if (regex.test(source)) return keyword;
  }
  return '';
}

export function getCaseWwwExplicitCategory(notes = '') {
  const text = normalizeCell(notes);
  if (!text) return '';
  if (caseWwwHasDormantFundsLanguage(text)) return 'Dormant Wallet';
  if (/A potential subpoenable path that could be explored if other paths prove unsuccessful/i.test(text) || /Low value path/i.test(text)) return 'Lost/Dismissed';
  if ((/^Unknown\./i.test(text) && (/\bpath\s+ends\b/i.test(text) || /\bservice address\b/i.test(text) || /\bexchange hot wallet\b/i.test(text))) || /Potential Service/i.test(text)) return 'Unknown';
  return '';
}

export function classifyCaseWwwPathEnding(pathEndingRow = [], entry = {}, keywordSets = {}, options = {}) {
  const traceDirection = normalizeCaseTraceDirection(options.traceDirection)
    || getCaseTraceDirectionForEntry(entry, CASE_TRACE_DIRECTION_FORWARD);
  const isReverse = traceDirection === CASE_TRACE_DIRECTION_REVERSE;
  const rowNotes = normalizeCell(pathEndingRow[9]);
  const entryNotes = getCaseWwwEntryNotes(entry);
  const attribution = getCaseTraceDirectionRelevantAttribution(pathEndingRow, traceDirection)
    || getCaseTraceDirectionRelevantAttribution(entry.firstRow || [], traceDirection);
  const reverseCaseNotes = isReverse
    ? [normalizeCell(pathEndingRow[22]), ...(entry.rows || []).map(item => normalizeCell(item?.row?.[22]))].filter(Boolean).join(' | ')
    : '';
  const combined = [rowNotes, attribution, entryNotes, reverseCaseNotes].filter(Boolean).join(' | ');
  const explicitCategory = getCaseWwwExplicitCategory(rowNotes) || (!rowNotes ? getCaseWwwExplicitCategory(entryNotes) : '');
  const exchangeEntity = findCaseWwwEntityInText(combined, keywordSets.exchange || []);
  const nonExchangeEntity = findCaseWwwEntityInText(combined, keywordSets.nonExchange || []);
  const entity = exchangeEntity || nonExchangeEntity || attribution || '';
  const finish = result => isReverse ? { ...result, traceDirection: CASE_TRACE_DIRECTION_REVERSE } : result;
  if (explicitCategory) return finish({ category: explicitCategory, entity });
  if (exchangeEntity) return finish({ category: 'Exchange', entity: exchangeEntity });
  if (nonExchangeEntity) return finish({ category: 'Non-Exchange', entity: nonExchangeEntity });
  return finish({ category: entity ? 'Other' : 'Other', entity });
}

export function getCaseWwwPathEndingAmountToken(pathEndingRow = [], entry = {}) {
  return parseCaseWwwFundsFollowed(pathEndingRow?.[12]) ||
    parseCaseWwwFundsFollowed(entry?.firstRow?.[12]) ||
    parseCaseWwwAmountTokenFromColumns(pathEndingRow?.[5], pathEndingRow?.[6]) ||
    parseCaseWwwAmountTokenFromColumns(entry?.firstRow?.[5], entry?.firstRow?.[6]);
}

export function getCaseWwwRowOrEntryFromHash(row = [], entry = {}) {
  return normalizeCell(row?.[14]) || normalizeCell(entry?.firstRow?.[14]);
}

export function getCaseWwwRowPathLabel(row = [], entry = {}) {
  return normalizeCell(row?.[0]) || normalizeCell(entry?.pathLabel);
}

export function parseCaseWwwContinuationHashes(text = '') {
  const match = normalizeCell(text).match(/continues\s+as\s+(.+)$/i);
  if (!match) return [];
  return match[1].split(/[,;]+/).map(value => normalizeCaseWwwHash(value.replace(/\.$/, ''))).filter(Boolean);
}

export function caseWwwContinuationIncludesHash(text = '', hash = '') {
  const normalized = normalizeCaseWwwHash(hash);
  return !!normalized && parseCaseWwwContinuationHashes(text).includes(normalized);
}

export function caseWwwRowHasSwapNote(row = []) {
  return /\bswap(?:ped|ping)?\b/i.test(normalizeCell(row?.[9]));
}

export function caseWwwRowHasBridgeNote(row = []) {
  return /\bbridg(?:ed|ing)?\b/i.test(normalizeCell(row?.[9]));
}

export function parseCaseWwwSwapInfoFromRow(row = []) {
  if (!caseWwwRowHasSwapNote(row)) return null;
  const output = parseCaseWwwAmountTokenFromColumns(row?.[5], row?.[6]);
  if (!output || output.amount === 0) return null;
  const notes = normalizeCell(row?.[9]);
  const directionMatch = notes.match(/swapp?(?:ed|ing)?\s+from\s+([A-Za-z0-9._:/-]+)\s+to\s+([A-Za-z0-9._:/-]+)/i);
  if (!directionMatch) return null;
  const sourceToken = normalizeCell(directionMatch[1]);
  const targetToken = normalizeCell(directionMatch[2]);
  if (!sourceToken) return null;
  const sourceAmountRegex = new RegExp(`(-?\\d[\\d,]*(?:\\.\\d+)?)\\s*${String(sourceToken).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
  const sourceAmountMatch = notes.match(sourceAmountRegex);
  const sourceAmount = sourceAmountMatch ? parseCaseWwwNumeric(sourceAmountMatch[1]) : null;
  if (!Number.isFinite(sourceAmount) || sourceAmount === 0) return null;
  return { sourceAmount, sourceToken, outputAmount: output.amount, outputToken: output.token, noteTargetToken: targetToken };
}

export function caseWwwEstimateSwapConversion(row = [], currentAmount = null, currentToken = '') {
  const swapInfo = parseCaseWwwSwapInfoFromRow(row);
  if (!swapInfo || !caseWwwTokenMatches(swapInfo.outputToken, currentToken) || !Number.isFinite(currentAmount) || swapInfo.outputAmount === 0) return null;
  return { upstreamAmount: currentAmount * (swapInfo.sourceAmount / swapInfo.outputAmount), upstreamToken: swapInfo.sourceToken };
}

export function caseWwwGetEntryAmountTokenForToken(entry = {}, preferredToken = '') {
  const normalizedPreferred = normalizeCaseWwwToken(preferredToken);
  const values = (entry.rows || []).map(item => ({ row: item.row, amountToken: parseCaseWwwAmountTokenFromColumns(item.row?.[5], item.row?.[6]) })).filter(item => item.amountToken);
  if (!values.length) return null;
  if (normalizedPreferred) {
    const preferred = values.find(item => caseWwwTokenMatches(item.amountToken.token, normalizedPreferred));
    if (preferred) return { ...preferred.amountToken, row: preferred.row };
  }
  return { ...values[0].amountToken, row: values[0].row };
}

export function caseWwwEstimateBridgeConversion(previousRow = [], currentEntry = {}, currentAmount = null, currentToken = '') {
  if (!caseWwwRowHasBridgeNote(previousRow)) return null;
  const bridgeSource = parseCaseWwwAmountTokenFromColumns(previousRow?.[5], previousRow?.[6]);
  const bridgeTarget = caseWwwGetEntryAmountTokenForToken(currentEntry, currentToken);
  if (!bridgeSource || !bridgeTarget || !caseWwwTokenMatches(bridgeTarget.token, currentToken) || !Number.isFinite(currentAmount) || bridgeTarget.amount === 0) return null;
  return { upstreamAmount: currentAmount * (bridgeSource.amount / bridgeTarget.amount), upstreamToken: bridgeSource.token };
}

export function caseWwwPathLabelAffinity(candidatePathLabel = '', currentPathLabel = '') {
  const candidate = normalizeCell(candidatePathLabel).toLowerCase();
  const current = normalizeCell(currentPathLabel).toLowerCase();
  if (!candidate || !current) return 0;
  if (current.startsWith(candidate)) return candidate.length;
  if (candidate.startsWith(current)) return current.length / 2;
  if (candidate[0] === current[0]) return 0.25;
  return 0;
}

export function caseWwwFindSwapRowInEntryForToken(entry = {}, currentToken = '', preferredTargetHash = '') {
  let best = null;
  (entry.rows || []).forEach((item, rowIndex) => {
    const row = item.row || [];
    const swapInfo = parseCaseWwwSwapInfoFromRow(row);
    if (!swapInfo || !caseWwwTokenMatches(swapInfo.outputToken, currentToken)) return;
    let score = 100;
    if (preferredTargetHash && caseWwwContinuationIncludesHash(row[16], preferredTargetHash)) score += 30;
    if (isCaseWwwPathEndingRow(row)) score += 5;
    if (rowIndex === 0) score += 1;
    if (!best || score > best.score || (score === best.score && rowIndex < best.rowIndex)) best = { row, rowIndex, score };
  });
  return best?.row || null;
}

export function caseWwwSelectMatchingEntryForFromHash(candidateEntries = [], currentEntry = {}, currentRow = [], currentToken = '') {
  const currentHash = normalizeCaseWwwHash(currentEntry.txnHash);
  const currentPathLabel = getCaseWwwRowPathLabel(currentRow, currentEntry);
  let best = null;
  candidateEntries.forEach((entry) => {
    (entry.rows || []).forEach((item, rowIndex) => {
      const row = item.row || [];
      let score = 0;
      const distance = Math.abs((entry.index || 0) - (currentEntry.index || 0));
      if (caseWwwContinuationIncludesHash(row[16], currentHash)) score += 120;
      if (caseWwwRowHasBridgeNote(row)) score += 30;
      if (caseWwwRowHasSwapNote(row) && caseWwwTokenMatches(row[6], currentToken)) score += 25;
      if (caseWwwTokenMatches(row[6], currentToken)) score += 10;
      score += caseWwwPathLabelAffinity(entry.pathLabel, currentPathLabel);
      if ((entry.index || 0) < (currentEntry.index || 0)) score += 6;
      else if ((entry.index || 0) > (currentEntry.index || 0)) score += 3;
      score += Math.max(0, 4 - distance * 0.1);
      if (rowIndex === 0) score += 2;
      if (!best || score > best.score || (score === best.score && distance < best.distance)) best = { entry, row, score, distance };
    });
  });
  if (best && best.score > 0) return best;
  const fallback = candidateEntries.slice().sort((a, b) => Math.abs((a.index || 0) - (currentEntry.index || 0)) - Math.abs((b.index || 0) - (currentEntry.index || 0)))[0];
  return fallback ? { entry: fallback, row: fallback.firstRow || [], score: 0, distance: Math.abs((fallback.index || 0) - (currentEntry.index || 0)) } : null;
}

export function buildCaseWwwManualSnapshot(entry = {}, row = [], amount = null, token = '', fromHash = '') {
  return {
    traceStoppedAt: `${getCaseWwwRowPathLabel(row, entry) || '(blank path)'} / ${normalizeCell(row?.[1]) || normalizeCell(entry.txnHash) || '(blank hash)'}`,
    amount: Number.isFinite(amount) ? amount : null,
    token: normalizeCell(token),
    fromHash: normalizeCell(fromHash) || getCaseWwwRowOrEntryFromHash(row, entry)
  };
}

export function caseWwwTraceAmountToMainToken({ currentEntry, currentRow, currentAmount, currentToken, mainToken, segmentEntries, visited, depth }) {
  if (!Number.isFinite(currentAmount) || !normalizeCell(currentToken)) {
    return { resolved: false, issue: 'The path ending did not have a usable amount/token pair for tracing.', manualReview: buildCaseWwwManualSnapshot(currentEntry, currentRow, currentAmount, currentToken) };
  }
  if (caseWwwTokenMatches(currentToken, mainToken)) return { resolved: true, amountInMainToken: currentAmount };
  if (depth >= CASE_WHAT_WENT_WHERE_MAX_TRACE_DEPTH) {
    return { resolved: false, issue: 'Tracing reached the maximum depth before the main-transaction token was found.', manualReview: buildCaseWwwManualSnapshot(currentEntry, currentRow, currentAmount, currentToken) };
  }
  const fromHash = getCaseWwwRowOrEntryFromHash(currentRow, currentEntry);
  const visitKey = [currentEntry.index, normalizeCaseWwwToken(currentToken), normalizeCaseWwwHash(fromHash)].join('::');
  if (visited.has(visitKey)) {
    return { resolved: false, issue: 'A loop was encountered while tracing the path ending upstream.', manualReview: buildCaseWwwManualSnapshot(currentEntry, currentRow, currentAmount, currentToken, fromHash) };
  }
  visited.add(visitKey);
  const currentSwap = caseWwwFindSwapRowInEntryForToken(currentEntry, currentToken);
  if (currentSwap) {
    const conversion = caseWwwEstimateSwapConversion(currentSwap, currentAmount, currentToken);
    if (conversion) {
      return caseWwwTraceAmountToMainToken({ currentEntry, currentRow: currentSwap, currentAmount: conversion.upstreamAmount, currentToken: conversion.upstreamToken, mainToken, segmentEntries, visited, depth: depth + 1 });
    }
  }
  if (!fromHash) {
    return { resolved: false, issue: `The path ending token ${currentToken} did not match the main-transaction token ${mainToken}, and there was no from-hash to trace further.`, manualReview: buildCaseWwwManualSnapshot(currentEntry, currentRow, currentAmount, currentToken) };
  }
  const normalizedFromHash = normalizeCaseWwwHash(fromHash);
  const matchingEntries = (segmentEntries || []).filter(entry => entry.index !== currentEntry.index && normalizeCaseWwwHash(entry.txnHash) === normalizedFromHash);
  if (!matchingEntries.length) {
    return { resolved: false, issue: `The from-hash ${fromHash} could not be found within the same main-transaction section.`, manualReview: buildCaseWwwManualSnapshot(currentEntry, currentRow, currentAmount, currentToken, fromHash) };
  }
  const previousMatch = caseWwwSelectMatchingEntryForFromHash(matchingEntries, currentEntry, currentRow, currentToken);
  if (!previousMatch?.entry) {
    return { resolved: false, issue: `Could not determine which upstream entry matched from-hash ${fromHash}.`, manualReview: buildCaseWwwManualSnapshot(currentEntry, currentRow, currentAmount, currentToken, fromHash) };
  }
  const bridgeConversion = caseWwwEstimateBridgeConversion(previousMatch.row, currentEntry, currentAmount, currentToken);
  if (bridgeConversion) {
    return caseWwwTraceAmountToMainToken({ currentEntry: previousMatch.entry, currentRow: previousMatch.row, currentAmount: bridgeConversion.upstreamAmount, currentToken: bridgeConversion.upstreamToken, mainToken, segmentEntries, visited, depth: depth + 1 });
  }
  const previousSwap = caseWwwFindSwapRowInEntryForToken(previousMatch.entry, currentToken, currentEntry.txnHash);
  if (previousSwap) {
    const conversion = caseWwwEstimateSwapConversion(previousSwap, currentAmount, currentToken);
    if (conversion) {
      return caseWwwTraceAmountToMainToken({ currentEntry: previousMatch.entry, currentRow: previousSwap, currentAmount: conversion.upstreamAmount, currentToken: conversion.upstreamToken, mainToken, segmentEntries, visited, depth: depth + 1 });
    }
  }
  return caseWwwTraceAmountToMainToken({ currentEntry: previousMatch.entry, currentRow: previousMatch.row, currentAmount, currentToken, mainToken, segmentEntries, visited, depth: depth + 1 });
}

export function evaluateCaseWwwUsdForPathEnding({ pathEndingRow, entry, mainTransaction, segmentEntries }) {
  const pathAmountToken = getCaseWwwPathEndingAmountToken(pathEndingRow, entry);
  if (pathAmountToken && caseWwwIsStablecoin(pathAmountToken.token)) {
    return { resolved: true, usdValue: pathAmountToken.amount, usdText: formatCaseWwwUsd(pathAmountToken.amount), resolution: 'stablecoin' };
  }
  const existingUsd = parseCaseWwwUsdReference(pathEndingRow?.[7]);
  if (Number.isFinite(existingUsd)) {
    return { resolved: true, usdValue: existingUsd, usdText: formatCaseWwwUsd(existingUsd), resolution: 'existing_column_h' };
  }
  if (!mainTransaction?.isUsable) {
    return { resolved: false, issue: 'The related main transaction did not have a usable amount/token/USDT trio in Columns F-H or M/H.', manualReview: buildCaseWwwManualSnapshot(entry, pathEndingRow, pathAmountToken?.amount ?? null, pathAmountToken?.token || '') };
  }
  if (!pathAmountToken) {
    return { resolved: false, issue: 'The path ending did not have a usable Funds Followed or amount/token value.', manualReview: buildCaseWwwManualSnapshot(entry, pathEndingRow) };
  }
  let amountInMainToken = null;
  if (caseWwwTokenMatches(pathAmountToken.token, mainTransaction.referenceToken)) {
    amountInMainToken = pathAmountToken.amount;
  } else {
    const trace = caseWwwTraceAmountToMainToken({ currentEntry: entry, currentRow: pathEndingRow, currentAmount: pathAmountToken.amount, currentToken: pathAmountToken.token, mainToken: mainTransaction.referenceToken, segmentEntries, visited: new Set(), depth: 0 });
    if (!trace.resolved || !Number.isFinite(trace.amountInMainToken)) {
      return { resolved: false, issue: trace.issue || 'The swap/bridge history could not be traced back to the main transaction token.', manualReview: trace.manualReview || buildCaseWwwManualSnapshot(entry, pathEndingRow, pathAmountToken.amount, pathAmountToken.token) };
    }
    amountInMainToken = trace.amountInMainToken;
  }
  const usdValue = amountInMainToken * (mainTransaction.currentUsdValue / mainTransaction.referenceAmount);
  if (!Number.isFinite(usdValue)) {
    return { resolved: false, issue: 'The final USD valuation could not be calculated from the derived token ratio.', manualReview: buildCaseWwwManualSnapshot(entry, pathEndingRow, amountInMainToken, mainTransaction.referenceToken) };
  }
  return { resolved: true, usdValue, usdText: formatCaseWwwUsd(usdValue), resolution: 'main_reference_ratio', amountInMainToken };
}

export function buildCaseWwwOutputRow(pathEndingRow = [], entryFirstRow = [], usdText = '') {
  const row = Array(CASE_WHAT_WENT_WHERE_COLUMN_COUNT).fill('');
  row[0] = normalizeCell(pathEndingRow[0]) || normalizeCell(entryFirstRow[0]);
  row[1] = normalizeCell(pathEndingRow[1]) || normalizeCell(entryFirstRow[1]);
  row[2] = '';
  row[3] = normalizeCell(pathEndingRow[3]) || normalizeCell(pathEndingRow[2]) || normalizeCell(entryFirstRow[3]) || normalizeCell(entryFirstRow[2]);
  row[4] = normalizeCell(pathEndingRow[4]) || normalizeCell(entryFirstRow[4]);
  row[5] = normalizeCell(pathEndingRow[5]) || normalizeCell(entryFirstRow[5]);
  row[6] = normalizeCell(pathEndingRow[6]) || normalizeCell(entryFirstRow[6]);
  row[7] = normalizeCell(usdText) || normalizeCell(pathEndingRow[7]);
  row[8] = '';
  row[9] = normalizeCell(pathEndingRow[9]);
  row[10] = '';
  row[11] = '';
  row[12] = normalizeCell(pathEndingRow[12]) || normalizeCell(entryFirstRow[12]) || 'Funds followed';
  row[13] = '';
  row[14] = normalizeCell(pathEndingRow[14]) || normalizeCell(entryFirstRow[14]);
  row[15] = '';
  row[16] = normalizeCell(pathEndingRow[16]);
  return row;
}

export function formatCaseWwwPathEndingReference(pathEndingRow = [], entry = {}) {
  return `${getCaseWwwRowPathLabel(pathEndingRow, entry) || '(blank path)'} / ${normalizeCell(pathEndingRow?.[1]) || normalizeCell(entry.txnHash) || '(blank hash)'}`;
}

export function buildCaseWwwTokenBreakdown(tokenTotals = new Map()) {
  return Array.from(tokenTotals.entries())
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([token, amount]) => formatCaseWwwTokenAmount(amount, token))
    .filter(Boolean)
    .join(', ');
}

export function addCaseWwwTokenTotal(map, token = '', amount = null) {
  if (!Number.isFinite(amount) || !normalizeCell(token)) return;
  const normalizedToken = normalizeCell(token);
  map.set(normalizedToken, (map.get(normalizedToken) || 0) + amount);
}

export function buildCaseWhatWentWhereSummaryRows(categoryCounts = {}) {
  return CASE_WHAT_WENT_WHERE_CATEGORY_ORDER.filter(category => categoryCounts[category]).map(category => [category, categoryCounts[category]]);
}

export function buildCaseWhatWentWhereState(mainData = [], effectiveCurator = {}, workflowState = {}, options = {}) {
  const rows = normalizeCaseMatrix(mainData);
  const referenceRows = buildCaseWwwReferenceRows(rows);
  const entries = normalizeCaseWwwEntries(parseCaseEntries(rows));
  const lookup = buildCaseWwwEntryLookup(entries);
  const keywordSets = {
    exchange: buildCaseWwwEntityKeywords(effectiveCurator, 'exchange'),
    nonExchange: buildCaseWwwEntityKeywords(effectiveCurator, 'nonExchange')
  };
  const traceDirectionContextByRowIndex = buildCaseTraceDirectionContextByRowIndex(rows);
  const outputRows = [];
  const pathEndingRows = [];
  const manualValuationRows = [];
  const categoryCounts = {};
  const summaryByCategory = new Map();
  const summaryByEntity = new Map();
  const mainCoverageRows = [];
  let matchedMainTransactions = 0;
  let generatedPathEndingCount = 0;
  let valuationResolvedCount = 0;
  let valuationUnresolvedCount = 0;
  const warnings = [];
  const mainEntryIndices = referenceRows.map(row => findCaseWwwEntryIndexForMain(row, entries, lookup));

  referenceRows.forEach((mainTransaction, mainIndex) => {
    const currentEntryIndex = mainEntryIndices[mainIndex];
    const foundTokenTotals = new Map();
    const coverage = {
      key: mainTransaction.key,
      mainIndex,
      pathLabel: mainTransaction.pathLabel,
      transactionHash: mainTransaction.transactionHash,
      originalUsdValue: mainTransaction.currentUsdValue,
      originalReferenceAmount: mainTransaction.referenceAmount,
      originalReferenceToken: mainTransaction.referenceToken,
      recoveredUsdValue: 0,
      pathEndingCount: 0,
      resolvedCount: 0,
      unresolvedCount: 0,
      notes: []
    };
    if (currentEntryIndex === undefined) {
      warnings.push(`Main transaction ${mainTransaction.pathLabel || mainIndex + 1} / ${mainTransaction.transactionHash || '(blank hash)'} was not found in the full case entries.`);
      coverage.notes.push('Main transaction not found in full case data.');
      mainCoverageRows.push(coverage);
      return;
    }

    matchedMainTransactions += 1;
    const currentEntry = entries[currentEntryIndex];
    (currentEntry.rows || []).forEach(item => outputRows.push(normalizeCaseMatrix([item.row], 1, CASE_WHAT_WENT_WHERE_COLUMN_COUNT)[0].slice(0, CASE_WHAT_WENT_WHERE_COLUMN_COUNT)));

    let nextEntryIndex = entries.length;
    for (let i = mainIndex + 1; i < referenceRows.length; i += 1) {
      const candidateIndex = mainEntryIndices[i];
      if (candidateIndex !== undefined && candidateIndex > currentEntryIndex) {
        nextEntryIndex = candidateIndex;
        break;
      }
    }
    const segmentEntries = entries.slice(currentEntryIndex, nextEntryIndex);

    for (let entryIndex = currentEntryIndex; entryIndex < nextEntryIndex; entryIndex += 1) {
      const entry = entries[entryIndex];
      const generatedRows = getCaseWwwGeneratedPathEndingRowsForEntry(entry);
      generatedRows.forEach((generated) => {
        const pathEndingRow = generated.row;
        const valuation = evaluateCaseWwwUsdForPathEnding({ pathEndingRow, entry, mainTransaction, segmentEntries });
        const traceDirection = normalizeCaseTraceDirection(
          traceDirectionContextByRowIndex.get(generated.rowIndex)?.traceDirection
          || traceDirectionContextByRowIndex.get(entry.startRow)?.traceDirection
        ) || CASE_TRACE_DIRECTION_FORWARD;
        const classification = classifyCaseWwwPathEnding(pathEndingRow, entry, keywordSets, { traceDirection });
        const amountToken = getCaseWwwPathEndingAmountToken(pathEndingRow, entry);
        const outputRow = buildCaseWwwOutputRow(pathEndingRow, entry.firstRow, valuation.usdText || '');
        outputRow[10] = classification.category;
        outputRow[11] = classification.entity;
        outputRows.push(outputRow);
        generatedPathEndingCount += 1;
        coverage.pathEndingCount += 1;
        categoryCounts[classification.category] = (categoryCounts[classification.category] || 0) + 1;
        if (!summaryByCategory.has(classification.category)) summaryByCategory.set(classification.category, { category: classification.category, usdTotal: 0, unresolvedCount: 0, tokenTotals: new Map() });
        const categorySummary = summaryByCategory.get(classification.category);
        if (classification.entity) {
          const entityKey = `${classification.category}|${classification.entity}`;
          if (!summaryByEntity.has(entityKey)) summaryByEntity.set(entityKey, { category: classification.category, entity: classification.entity, usdTotal: 0, unresolvedCount: 0, tokenTotals: new Map() });
        }
        if (valuation.resolved && Number.isFinite(valuation.usdValue)) {
          valuationResolvedCount += 1;
          coverage.resolvedCount += 1;
          coverage.recoveredUsdValue += valuation.usdValue;
          categorySummary.usdTotal += valuation.usdValue;
          if (classification.entity) summaryByEntity.get(`${classification.category}|${classification.entity}`).usdTotal += valuation.usdValue;
        } else {
          valuationUnresolvedCount += 1;
          coverage.unresolvedCount += 1;
          categorySummary.unresolvedCount += 1;
          if (classification.entity) summaryByEntity.get(`${classification.category}|${classification.entity}`).unresolvedCount += 1;
          manualValuationRows.push({
            key: `manual-www-${caseBuddyHashString(`${entry.index}|${generated.rowIndex}|${valuation.issue || ''}`)}`,
            pathEndingReference: formatCaseWwwPathEndingReference(pathEndingRow, entry),
            traceStoppedAt: valuation.manualReview?.traceStoppedAt || formatCaseWwwPathEndingReference(pathEndingRow, entry),
            amountText: formatCaseWwwTokenAmount(valuation.manualReview?.amount ?? amountToken?.amount, valuation.manualReview?.token || amountToken?.token || ''),
            fromHash: valuation.manualReview?.fromHash || getCaseWwwRowOrEntryFromHash(pathEndingRow, entry),
            issue: valuation.issue || 'Manual valuation review needed.'
          });
        }
        addCaseWwwTokenTotal(foundTokenTotals, amountToken?.token, amountToken?.amount);
        addCaseWwwTokenTotal(categorySummary.tokenTotals, amountToken?.token, amountToken?.amount);
        if (classification.entity) addCaseWwwTokenTotal(summaryByEntity.get(`${classification.category}|${classification.entity}`).tokenTotals, amountToken?.token, amountToken?.amount);
        pathEndingRows.push({
          key: `www-path-ending-${caseBuddyHashString(`${entry.index}|${generated.rowIndex}|${outputRow.join('|')}`)}`,
          mainKey: mainTransaction.key,
          mainPathLabel: mainTransaction.pathLabel,
          mainTransactionHash: mainTransaction.transactionHash,
          rowNumber: (generated.rowIndex ?? entry.startRow) + 1,
          pathLabel: getCaseWwwRowPathLabel(pathEndingRow, entry),
          transactionHash: normalizeCell(pathEndingRow[1]) || entry.txnHash,
          toAddress: normalizeCell(outputRow[3]),
          category: classification.category,
          entity: classification.entity,
          amountText: amountToken ? formatCaseWwwTokenAmount(amountToken.amount, amountToken.token) : '',
          usdText: valuation.usdText || '',
          resolved: !!valuation.resolved,
          issue: valuation.issue || '',
          notes: normalizeCell(pathEndingRow[9]),
          synthetic: !!generated.synthetic,
          searchText: [mainTransaction.pathLabel, mainTransaction.transactionHash, outputRow[3], classification.category, classification.entity, amountToken?.token, valuation.usdText, valuation.issue, pathEndingRow[9]].map(value => normalizeCell(value).toLowerCase()).join(' ')
        });
      });
    }
    coverage.foundTokensText = buildCaseWwwTokenBreakdown(foundTokenTotals);
    if (!mainTransaction.isUsable) coverage.notes.push(mainTransaction.problemText || 'Main transaction Column H reference is incomplete.');
    mainCoverageRows.push(coverage);
  });

  const categorySummaryRows = Array.from(summaryByCategory.values()).sort((a, b) => CASE_WHAT_WENT_WHERE_CATEGORY_ORDER.indexOf(a.category) - CASE_WHAT_WENT_WHERE_CATEGORY_ORDER.indexOf(b.category)).map(row => ({ ...row, usdDisplay: formatCaseWwwUsd(row.usdTotal), tokensText: buildCaseWwwTokenBreakdown(row.tokenTotals) }));
  const entitySummaryRows = Array.from(summaryByEntity.values()).sort((a, b) => a.category.localeCompare(b.category) || b.usdTotal - a.usdTotal || a.entity.localeCompare(b.entity)).map(row => ({ ...row, usdDisplay: formatCaseWwwUsd(row.usdTotal), tokensText: buildCaseWwwTokenBreakdown(row.tokenTotals) }));
  const overallUsdTotal = pathEndingRows.reduce((sum, row) => sum + (parseCaseWwwUsdReference(row.usdText) || 0), 0);
  const missingUsdReferenceCount = referenceRows.filter(row => !row.isUsable).length;
  const buildIsCurrent = referenceRows.length > 0 && missingUsdReferenceCount === 0;
  const currentBasisHash = `what-went-where-${caseBuddyHashString(JSON.stringify({
    references: referenceRows.map(row => [row.rowIndex, row.pathLabel, row.transactionHash, row.currentColumnH, row.referenceAmount, row.referenceToken]),
    outputRows,
    categorySummaryRows: categorySummaryRows.map(row => [row.category, row.usdTotal, row.unresolvedCount, row.tokensText]),
    entitySummaryRows: entitySummaryRows.map(row => [row.category, row.entity, row.usdTotal, row.unresolvedCount, row.tokensText]),
    manualValuationRows: manualValuationRows.map(row => [row.pathEndingReference, row.amountText, row.fromHash, row.issue])
  }))}`;
  const review = workflowState?.whatWentWhereReview || null;
  const reviewIsCurrent = !!review?.reviewedAtUtc && review.basisHash === currentBasisHash && Number(review.revision) === Number(options.caseDataRevision || 0);

  return {
    caseId: normalizeCell(options.caseId),
    referenceRows,
    referenceRowCount: referenceRows.length,
    usableReferenceCount: referenceRows.filter(row => row.isUsable).length,
    missingUsdReferenceCount,
    matchedMainTransactions,
    generatedPathEndingCount,
    valuationResolvedCount,
    valuationUnresolvedCount,
    outputRows,
    pathEndingRows,
    summaryRows: buildCaseWhatWentWhereSummaryRows(categoryCounts),
    categorySummaryRows,
    entitySummaryRows,
    manualValuationRows,
    mainCoverageRows: mainCoverageRows.map((row) => {
      const originalUsd = Number(row.originalUsdValue);
      const recoveredUsd = Number(row.recoveredUsdValue) || 0;
      const coveragePercent = Number.isFinite(originalUsd) && originalUsd !== 0 ? (recoveredUsd / originalUsd) * 100 : null;
      return {
        ...row,
        originalUsdDisplay: Number.isFinite(originalUsd) ? formatCaseWwwUsd(originalUsd) : '',
        recoveredUsdDisplay: formatCaseWwwUsd(recoveredUsd),
        coveragePercentDisplay: Number.isFinite(coveragePercent) ? `${coveragePercent.toFixed(2)}%` : '—',
        gapDisplay: Number.isFinite(originalUsd) ? formatCaseWwwUsd(originalUsd - recoveredUsd) : '',
        foundTokensText: row.foundTokensText || '',
        notesText: (row.notes || []).filter(Boolean).join(' ')
      };
    }),
    warnings,
    overallUsdTotal,
    overallUsdDisplay: formatCaseWwwUsd(overallUsdTotal),
    buildIsCurrent,
    canBuild: buildIsCurrent,
    reviewIsCurrent,
    currentBasisHash,
    priceReferenceMetadata: getCaseBuddyHistoricalPriceReferenceMetadata(),
    summary: referenceRows.length === 0
      ? 'No top-level main transactions were found.'
      : missingUsdReferenceCount > 0
        ? `${referenceRows.length - missingUsdReferenceCount}/${referenceRows.length} top-level main transaction${referenceRows.length === 1 ? '' : 's'} have usable Column H USD values. ${missingUsdReferenceCount} still need attention before What Went Where can be reviewed.`
        : `What Went Where is buildable from ${referenceRows.length} top-level main transaction${referenceRows.length === 1 ? '' : 's'} and ${generatedPathEndingCount} generated path-ending row${generatedPathEndingCount === 1 ? '' : 's'}. ${valuationUnresolvedCount} generated path-ending valuation${valuationUnresolvedCount === 1 ? '' : 's'} need manual review.`
  };
}

export function buildCaseWhatWentWhereDeliverableRows(state = {}) {
  return [CASE_WHAT_WENT_WHERE_HEADERS.slice(), ...(Array.isArray(state.outputRows) ? state.outputRows : [])];
}

export function buildCaseWhatWentWhereValuationAuditRows(state = {}) {
  return [
    ['Case Buddy What Went Where Valuation Audit', '', '', '', '', '', '', '', '', 'Audit only. Built from current Main Data and Column H top-level USD references; Main Data is not modified by staging this audit.'],
    ['Status', state.summary || '', 'Basis', state.currentBasisHash || '', 'Generated Path Endings', state.generatedPathEndingCount || 0, 'Resolved', state.valuationResolvedCount || 0, 'Unresolved', state.valuationUnresolvedCount || 0],
    ['Overall USD Total', state.overallUsdDisplay || '', 'Matched Main Transactions', state.matchedMainTransactions || 0, 'Missing Column H References', state.missingUsdReferenceCount || 0],
    [],
    ['Category', 'USD Total', 'Token Breakdown', 'Unresolved Rows'],
    ...(Array.isArray(state.categorySummaryRows) ? state.categorySummaryRows : []).map(row => [row.category, row.usdDisplay, row.tokensText, row.unresolvedCount || 0]),
    [],
    ['Category', 'Entity', 'USD Total', 'Token Breakdown', 'Unresolved Rows'],
    ...(Array.isArray(state.entitySummaryRows) ? state.entitySummaryRows : []).map(row => [row.category, row.entity, row.usdDisplay, row.tokensText, row.unresolvedCount || 0]),
    [],
    ['Manual Valuation Review'],
    ['Path Ending', 'Trace Stopped At', 'Amount To Value', 'From Hash', 'Issue'],
    ...(Array.isArray(state.manualValuationRows) ? state.manualValuationRows : []).map(row => [row.pathEndingReference, row.traceStoppedAt, row.amountText, row.fromHash, row.issue]),
    [],
    ['Per Main Transaction Coverage'],
    ['Main Path', 'Transaction Hash', 'Original USD', 'Recovered USD', 'Gap', 'Coverage', 'Found Tokens', 'Rows', 'Notes'],
    ...(Array.isArray(state.mainCoverageRows) ? state.mainCoverageRows : []).map(row => [row.pathLabel, row.transactionHash, row.originalUsdDisplay, row.recoveredUsdDisplay, row.gapDisplay, row.coveragePercentDisplay, row.foundTokensText, `${row.resolvedCount || 0}/${row.pathEndingCount || 0} resolved`, row.notesText || ''])
  ];
}

export function buildCaseWhatWentWhereAuditRows(state = {}, actionLabel = 'What Went Where review') {
  return [
    ['Case Buddy What Went Where Review Audit', '', '', '', '', '', '', '', '', 'Audit only. What Went Where and its valuation audit are generated from current Main Data and support state.'],
    ['Action', actionLabel, 'Reference Rows', state.referenceRowCount || 0, 'Usable References', state.usableReferenceCount || 0, 'Path Endings', state.generatedPathEndingCount || 0, 'Basis', state.currentBasisHash || ''],
    ['Status', state.summary || '', 'Resolved Valuations', state.valuationResolvedCount || 0, 'Unresolved Valuations', state.valuationUnresolvedCount || 0, 'Overall USD', state.overallUsdDisplay || ''],
    [],
    ...buildCaseWhatWentWhereValuationAuditRows(state)
  ];
}
